import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Flower, Sun, Wind, Heart, Leaf, MoveRight, Sparkles, Smile } from 'lucide-react';

const YogaWellness: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Serenity Flow";
    
    return (
        <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-600 selection:bg-[#ccfbf1] selection:text-[#0f766e]">
            {/* Soft Gradient Blobs */}
            <div className="fixed top-[-20%] left-[-10%] w-[70vw] h-[70vw] bg-[#ccfbf1] rounded-full blur-[100px] opacity-40 mix-blend-multiply z-0 animate-[pulse_10s_ease-in-out_infinite]"></div>
            <div className="fixed bottom-[-20%] right-[-10%] w-[70vw] h-[70vw] bg-[#fae8ff] rounded-full blur-[100px] opacity-40 mix-blend-multiply z-0 animate-[pulse_15s_ease-in-out_infinite_reverse]"></div>

            {/* Navbar */}
            <nav className="fixed w-full z-50 px-8 py-6 bg-white/50 backdrop-blur-md border-b border-white/40">
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2 text-[#0f766e]">
                        <Flower size={32} strokeWidth={1.5} />
                        <span className="font-light text-2xl tracking-wide">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex gap-10 text-sm font-medium text-slate-500">
                         <a href="#" className="hover:text-[#0f766e] transition-colors relative group">
                             Classes
                             <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#0f766e] group-hover:w-full transition-all"></span>
                         </a>
                         <a href="#" className="hover:text-[#0f766e] transition-colors relative group">
                             Retreats
                             <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#0f766e] group-hover:w-full transition-all"></span>
                         </a>
                         <a href="#" className="hover:text-[#0f766e] transition-colors relative group">
                             Teachers
                             <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#0f766e] group-hover:w-full transition-all"></span>
                         </a>
                    </div>

                    <Button className="rounded-full bg-[#0f766e] hover:bg-[#115e59] text-white px-8 h-12 shadow-lg shadow-teal-900/10">
                        Free Trial
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 pt-48 pb-32 px-8 max-w-6xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm text-xs font-medium text-[#0f766e] mb-8 animate-in fade-in zoom-in duration-700">
                    <Sparkles size={14} /> New mindfulness courses added
                </div>
                
                <h1 className="text-6xl md:text-8xl font-light text-[#134e4a] mb-8 leading-[1.1]">
                    Find your <br/>
                    <span className="font-serif italic text-[#c084fc] relative inline-block">
                        Inner Peace.
                        <div className="absolute top-0 right-[-20px] animate-bounce delay-700">
                            <Sun size={24} className="text-yellow-400 fill-yellow-200" />
                        </div>
                    </span>
                </h1>
                
                <p className="text-xl font-light text-slate-500 max-w-2xl mx-auto mb-12 leading-relaxed">
                    A sanctuary for movement, breath, and being. Join our community and discover a holistic approach to wellness.
                </p>
                
                <div className="flex justify-center gap-4">
                     <Button size="lg" className="rounded-full bg-[#2dd4bf] hover:bg-[#14b8a6] text-white px-8 text-lg font-light h-14 shadow-xl hover:-translate-y-1 transition-all">
                         Start Practice
                     </Button>
                     <Button size="lg" variant="ghost" className="rounded-full text-[#134e4a] hover:bg-white/50 px-8 text-lg font-light h-14">
                         Watch Video <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center ml-2 shadow-sm"><div className="border-t-[6px] border-b-[6px] border-l-[10px] border-transparent border-l-[#134e4a] ml-1"></div></div>
                     </Button>
                </div>
            </header>

            {/* Card Section with Hover Effects */}
            <section className="relative z-10 px-8 py-20 bg-white/40 backdrop-blur-md rounded-[3rem] mx-4 mb-20 shadow-sm border border-white/60">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Wind, title: "Breathwork", color: "text-blue-400", bg: "bg-blue-50" },
                        { icon: Sun, title: "Morning Flow", color: "text-orange-400", bg: "bg-orange-50" },
                        { icon: Heart, title: "Meditation", color: "text-rose-400", bg: "bg-rose-50" }
                    ].map((item, i) => (
                        <div key={i} className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group">
                             <div className={`w-16 h-16 ${item.bg} rounded-2xl flex items-center justify-center ${item.color} mb-6 group-hover:scale-110 transition-transform`}>
                                 <item.icon size={32} />
                             </div>
                             <h3 className="text-xl font-medium text-slate-800 mb-3">{item.title}</h3>
                             <p className="text-slate-400 font-light leading-relaxed mb-6">
                                 Reconnect with yourself through guided sessions designed for all levels.
                             </p>
                             <div className="flex items-center gap-2 text-sm font-medium text-slate-500 group-hover:gap-4 transition-all">
                                 Learn more <MoveRight size={16} />
                             </div>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 py-20 px-8 text-center bg-white border-t border-slate-100">
                 <div className="max-w-7xl mx-auto">
                     <div className="mb-12">
                         <Flower size={48} className="mx-auto text-[#0f766e] mb-4" strokeWidth={1} />
                         <h2 className="text-3xl font-light text-[#134e4a] mb-2">{schoolName}</h2>
                         <p className="text-slate-400 font-light">Breathe in, breathe out.</p>
                     </div>
                     
                     <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-500 font-medium">
                         <a href="#" className="hover:text-[#0f766e]">Privacy Policy</a>
                         <a href="#" className="hover:text-[#0f766e]">Terms of Service</a>
                         <a href="#" className="hover:text-[#0f766e]">Cookie Settings</a>
                         <a href="#" className="hover:text-[#0f766e]">Accessibility</a>
                     </div>
                     
                     <div className="mt-12 text-slate-300 text-xs">
                         &copy; 2026 {schoolName}. All rights reserved. NAMASTE.
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default YogaWellness;
